"use client";

import { useMemo, useState } from "react";
import { loadStripe, type StripeElementsOptions } from "@stripe/stripe-js";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatPrice } from "@/types/product";

export function StripeCardPayment({
  clientSecret,
  publishableKey,
  amount,
  currency,
  submitLabel,
  defaultName = "",
  defaultEmail = "",
  collectContact = true,
  onSucceeded,
}: {
  clientSecret: string;
  publishableKey: string;
  amount: number;
  currency?: string;
  submitLabel?: string;
  defaultName?: string;
  defaultEmail?: string;
  collectContact?: boolean;
  onSucceeded: () => void;
}) {
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);
  const options: StripeElementsOptions = { clientSecret };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CardForm
        clientSecret={clientSecret}
        amount={amount}
        currency={currency}
        submitLabel={submitLabel}
        defaultName={defaultName}
        defaultEmail={defaultEmail}
        collectContact={collectContact}
        onSucceeded={onSucceeded}
      />
    </Elements>
  );
}

function CardForm({
  clientSecret,
  amount,
  currency,
  submitLabel,
  defaultName,
  defaultEmail,
  collectContact,
  onSucceeded,
}: {
  clientSecret: string;
  amount: number;
  currency?: string;
  submitLabel?: string;
  defaultName: string;
  defaultEmail: string;
  collectContact: boolean;
  onSucceeded: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError("Card element not found");
      setProcessing(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: name || undefined,
          email: email || undefined,
        },
      },
    });

    if (confirmError) {
      setError(confirmError.message || "Payment failed");
      setProcessing(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      onSucceeded();
      return;
    }

    setError(`Payment status: ${paymentIntent?.status || "unknown"}`);
    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-4">
      {collectContact && (
        <>
          <div>
            <Label htmlFor="paymentCustomerName">Name</Label>
            <Input
              id="paymentCustomerName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
            />
          </div>
          <div>
            <Label htmlFor="paymentCustomerEmail">Email</Label>
            <Input
              id="paymentCustomerEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
        </>
      )}
      <div>
        <Label>Card details</Label>
        <div className="mt-1 rounded border border-[var(--color-border)] bg-white p-3">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#0f172a",
                  "::placeholder": { color: "#94a3b8" },
                },
              },
            }}
          />
        </div>
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <Button type="submit" size="lg" className="w-full" disabled={!stripe || processing}>
        {processing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            Processing…
          </>
        ) : (
          submitLabel || `Pay ${formatPrice(amount, null, currency)}`
        )}
      </Button>
    </form>
  );
}
